import {
  createUnvalidatedValidationSummary,
  type CoasterProject,
  type TrackBehavior,
  type TrackNode,
  type Vec3,
} from '../schema/index.ts';
import { makeOrderedSection, PHASE_2_NODE_LIMIT } from './topology.ts';
import { newStableId, setSectionBehavior, type TrackOperationResult } from './operations.ts';
import { assessCandidateProject, type CandidatePreflight } from './validation.ts';
import {
  generateSemanticElementPoints,
  isSemanticInversion,
  type SemanticControlPoint,
  type SemanticShapeElementId,
} from './semantic-elements.ts';

export type ElementPresetId =
  | 'station'
  | 'straight'
  | 'turn_left'
  | 'turn_right'
  | 'hill'
  | 'drop'
  | 'lift'
  | 'launch'
  | 'brake'
  | 'loop'
  | 'roll_left'
  | 'roll_right'
  | 'corkscrew_left'
  | 'corkscrew_right'
  | 'photo';

export interface ElementPresetDefinition {
  readonly id: ElementPresetId;
  readonly label: string;
  readonly category: 'shape' | 'system' | 'moment';
  readonly description: string;
}

export interface PresetCandidate {
  readonly preset: ElementPresetDefinition;
  readonly project: CoasterProject;
  readonly message: string;
  readonly warnings: readonly string[];
  readonly affectedNodeIds: readonly string[];
  readonly affectedSectionIds: readonly string[];
  readonly preflight: CandidatePreflight;
  readonly focusNodeId?: string;
}

export const ELEMENT_PRESETS: readonly ElementPresetDefinition[] = [
  {
    id: 'station',
    label: 'Station',
    category: 'system',
    description: 'Start and finish the ride.',
  },
  {
    id: 'straight',
    label: 'Straight',
    category: 'shape',
    description: 'Add a clean straight section.',
  },
  {
    id: 'turn_left',
    label: 'Left Turn',
    category: 'shape',
    description: 'Curve left with safe spacing.',
  },
  {
    id: 'turn_right',
    label: 'Right Turn',
    category: 'shape',
    description: 'Curve right with safe spacing.',
  },
  { id: 'hill', label: 'Hill', category: 'shape', description: 'Rise and fall for airtime.' },
  { id: 'drop', label: 'Drop', category: 'shape', description: 'Add a fast downward run.' },
  {
    id: 'lift',
    label: 'Lift Hill',
    category: 'system',
    description: 'Climb with a powered chain.',
  },
  { id: 'launch', label: 'Launch', category: 'system', description: 'Accelerate the train.' },
  { id: 'brake', label: 'Brakes', category: 'system', description: 'Control the approach speed.' },
  { id: 'loop', label: 'Loop', category: 'shape', description: 'Add a vertical inversion.' },
  {
    id: 'roll_left',
    label: 'Left Roll',
    category: 'shape',
    description: 'Roll one full turn left.',
  },
  {
    id: 'roll_right',
    label: 'Right Roll',
    category: 'shape',
    description: 'Roll one full turn right.',
  },
  {
    id: 'corkscrew_left',
    label: 'Left Corkscrew',
    category: 'shape',
    description: 'Twist through a compact left corkscrew.',
  },
  {
    id: 'corkscrew_right',
    label: 'Right Corkscrew',
    category: 'shape',
    description: 'Twist through a compact right corkscrew.',
  },
  {
    id: 'photo',
    label: 'Photo',
    category: 'moment',
    description: 'Mark the on-ride photo moment.',
  },
] as const;

function touch(project: CoasterProject): CoasterProject {
  project.metadata.updatedAt = new Date().toISOString();
  project.validation = createUnvalidatedValidationSummary();
  return project;
}

function definition(id: ElementPresetId): ElementPresetDefinition {
  return ELEMENT_PRESETS.find((preset) => preset.id === id)!;
}

function finalizeCandidate(
  base: CoasterProject,
  candidate: Omit<PresetCandidate, 'preflight'>,
): PresetCandidate {
  return {
    ...candidate,
    preflight: assessCandidateProject(
      base,
      candidate.project,
      candidate.affectedNodeIds,
      candidate.affectedSectionIds,
    ),
  };
}

function systemBehavior(id: ElementPresetId): TrackBehavior | null {
  switch (id) {
    case 'station':
      return { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 2.5 };
    case 'lift':
      return { kind: 'lift', targetSpeedMps: 5 };
    case 'launch':
      return { kind: 'launch', targetSpeedMps: 20, accelerationMps2: 7 };
    case 'brake':
      return { kind: 'brake', targetSpeedMps: 6, decelerationMps2: 5 };
    case 'photo':
      return { kind: 'photo' };
    default:
      return null;
  }
}

function blankStation(project: CoasterProject): PresetCandidate {
  const next = structuredClone(project);
  const positions: Vec3[] = [
    { x: -30, y: 4, z: 20 },
    { x: -22, y: 4, z: 20 },
    { x: -14, y: 4, z: 20 },
    { x: -6, y: 4, z: 20 },
  ];
  next.track.closed = false;
  next.track.nodes = positions.map((position) => ({
    id: newStableId('node'),
    position,
    bankDegrees: 0,
    rollWindingDegrees: 0,
  }));
  next.track.sections = next.track.nodes
    .slice(0, -1)
    .map((node, index) =>
      makeOrderedSection(
        newStableId('section'),
        node.id,
        next.track.nodes[index + 1]!.id,
        index === 1
          ? { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 2.5 }
          : { kind: 'normal' },
      ),
    );
  return finalizeCandidate(project, {
    preset: definition('station'),
    project: touch(next),
    message: 'Station placed. Build from the highlighted end.',
    warnings: [],
    affectedNodeIds: next.track.nodes.map((node) => node.id),
    affectedSectionIds: next.track.sections.map((section) => section.id),
    focusNodeId: next.track.nodes.at(-1)!.id,
  });
}

function vectorLength(vector: Vec3): number {
  return Math.hypot(vector.x, vector.y, vector.z);
}

function localFrame(from: Vec3, to: Vec3): { forward: Vec3; right: Vec3; distance: number } {
  const delta = { x: to.x - from.x, y: 0, z: to.z - from.z };
  const rawDistance = vectorLength(delta);
  const distance = Math.max(8, rawDistance);
  const forward =
    rawDistance > 0.001
      ? { x: delta.x / rawDistance, y: 0, z: delta.z / rawDistance }
      : { x: 1, y: 0, z: 0 };
  return { forward, right: { x: forward.z, y: 0, z: -forward.x }, distance };
}

interface AppendPoint {
  readonly forward: number;
  readonly side?: number;
  readonly height?: number;
  readonly bank?: number;
}

function isShapeElement(id: ElementPresetId): id is SemanticShapeElementId {
  return (
    id === 'straight' ||
    id === 'turn_left' ||
    id === 'turn_right' ||
    id === 'hill' ||
    id === 'drop' ||
    id === 'loop' ||
    id === 'roll_left' ||
    id === 'roll_right' ||
    id === 'corkscrew_left' ||
    id === 'corkscrew_right'
  );
}

function toAppendPoint(point: SemanticControlPoint): AppendPoint {
  return {
    forward: point.forwardM,
    side: point.sideM,
    height: point.heightM,
    bank: point.bankDeltaDegrees,
  };
}

function appendPoints(id: ElementPresetId): readonly AppendPoint[] {
  if (isShapeElement(id)) return generateSemanticElementPoints(id).map(toAppendPoint);
  switch (id) {
    case 'station':
      return [{ forward: 6 }, { forward: 12 }];
    case 'launch':
    case 'brake':
    case 'photo':
      return [{ forward: 6 }, { forward: 12 }];
    case 'lift':
      return [
        { forward: 5, height: 2.5 },
        { forward: 10, height: 6 },
        { forward: 15, height: 10 },
        { forward: 20, height: 14 },
      ];
  }
}

function appendToOpenEnd(
  project: CoasterProject,
  id: ElementPresetId,
): PresetCandidate | TrackOperationResult {
  if (project.track.closed || project.track.nodes.length < 2)
    return { ok: false, code: 'not_open', message: 'Start with a station first.' };
  const points = appendPoints(id);
  if (project.track.nodes.length + points.length > PHASE_2_NODE_LIMIT)
    return {
      ok: false,
      code: 'node_limit',
      message: `This editor supports up to ${PHASE_2_NODE_LIMIT} shape handles.`,
    };
  const next = structuredClone(project);
  const end = next.track.nodes.at(-1)!;
  const beforeEnd = next.track.nodes.at(-2)!;
  if (
    Math.hypot(end.position.x - beforeEnd.position.x, end.position.z - beforeEnd.position.z) < 0.5
  )
    return {
      ok: false,
      code: 'degenerate_endpoint',
      message: 'Move the highlighted end away from the previous handle before adding a piece.',
    };
  const frame = localFrame(beforeEnd.position, end.position);
  const inversion = isSemanticInversion(id);
  const generated = points.map<TrackNode>((point) => ({
    id: newStableId('node'),
    position: {
      x: end.position.x + frame.forward.x * point.forward + frame.right.x * (point.side ?? 0),
      y: Math.max(1, Math.min(65, end.position.y + (point.height ?? 0))),
      z: end.position.z + frame.forward.z * point.forward + frame.right.z * (point.side ?? 0),
    },
    bankDegrees: inversion
      ? end.bankDegrees
      : Math.max(-90, Math.min(90, end.bankDegrees + (point.bank ?? 0))),
    rollWindingDegrees: inversion
      ? end.rollWindingDegrees + (point.bank ?? 0)
      : end.rollWindingDegrees,
  }));
  const behavior = systemBehavior(id) ?? { kind: 'normal' as const };
  const chain = [end, ...generated];
  const additions = chain
    .slice(0, -1)
    .map((node, index) =>
      makeOrderedSection(
        newStableId('section'),
        node.id,
        chain[index + 1]!.id,
        structuredClone(behavior),
      ),
    );
  next.track.nodes.push(...generated);
  next.track.sections.push(...additions);
  return finalizeCandidate(project, {
    preset: definition(id),
    project: touch(next),
    message: `${definition(id).label} added. Keep building from the highlighted end.`,
    warnings: inversion
      ? ['Inversions are advanced pieces. Check the highlighted preview and run Build Checks.']
      : [],
    affectedNodeIds: [end.id, ...generated.map((node) => node.id)],
    affectedSectionIds: additions.map((section) => section.id),
    focusNodeId: generated.at(-1)!.id,
  });
}

function localPoints(id: ElementPresetId, chordLengthM: number): readonly SemanticControlPoint[] {
  return isShapeElement(id)
    ? generateSemanticElementPoints(id, {
        chordLengthM,
        includeExit: false,
        replacement: true,
      })
    : [];
}

function replaceSectionShape(
  project: CoasterProject,
  sectionId: string,
  id: ElementPresetId,
): TrackOperationResult {
  const sectionIndex = project.track.sections.findIndex((section) => section.id === sectionId);
  if (sectionIndex < 0)
    return { ok: false, code: 'missing_section', message: 'Select a track section first.' };
  const next = structuredClone(project);
  const section = next.track.sections[sectionIndex]!;
  const from = next.track.nodes[sectionIndex]!;
  const to = next.track.nodes[(sectionIndex + 1) % next.track.nodes.length]!;
  const frame = localFrame(from.position, to.position);
  const points = localPoints(id, frame.distance);
  const inversion = isSemanticInversion(id);
  if (project.track.nodes.length + points.length > PHASE_2_NODE_LIMIT)
    return {
      ok: false,
      code: 'node_limit',
      message: `This editor supports up to ${PHASE_2_NODE_LIMIT} track points.`,
    };
  const generated = points.map<TrackNode>((point) => ({
    id: newStableId('node'),
    position: {
      x: from.position.x + frame.forward.x * point.forwardM + frame.right.x * point.sideM,
      y: Math.max(
        1,
        Math.min(
          65,
          from.position.y + (to.position.y - from.position.y) * point.progress + point.heightM,
        ),
      ),
      z: from.position.z + frame.forward.z * point.forwardM + frame.right.z * point.sideM,
    },
    bankDegrees: inversion
      ? from.bankDegrees
      : Math.max(-90, Math.min(90, from.bankDegrees + point.bankDeltaDegrees)),
    rollWindingDegrees: inversion
      ? from.rollWindingDegrees + point.bankDeltaDegrees
      : from.rollWindingDegrees,
  }));
  next.track.nodes.splice(sectionIndex + 1, 0, ...generated);
  next.track.sections.splice(sectionIndex, 1);
  const chain = [from, ...generated, to];
  const replacementSections = chain
    .slice(0, -1)
    .map((node, index) =>
      makeOrderedSection(
        index === 0 ? section.id : newStableId('section'),
        node.id,
        chain[index + 1]!.id,
        index === 0 ? structuredClone(section.behavior) : { kind: 'normal' },
      ),
    );
  next.track.sections.splice(sectionIndex, 0, ...replacementSections);
  return { ok: true, project: touch(next), message: `${definition(id).label} ready.` };
}

export function buildPresetCandidate(
  project: CoasterProject,
  id: ElementPresetId,
  selectedSectionId: string | null,
): TrackOperationResult | PresetCandidate {
  if (id === 'station' && project.track.nodes.length === 0) return blankStation(project);
  if (!project.track.closed) return appendToOpenEnd(project, id);
  if (!selectedSectionId)
    return { ok: false, code: 'missing_section', message: 'Select a track section first.' };
  const behavior = systemBehavior(id);
  if (behavior) {
    const result = setSectionBehavior(project, selectedSectionId, behavior);
    if (!result.ok) return result;
    return finalizeCandidate(project, {
      preset: definition(id),
      project: result.project,
      message: result.message,
      warnings: [],
      affectedNodeIds: [],
      affectedSectionIds: [selectedSectionId],
    });
  }
  const result = replaceSectionShape(project, selectedSectionId, id);
  if (!result.ok) return result;
  const section = project.track.sections.find((candidate) => candidate.id === selectedSectionId);
  const originalNodeIds = new Set(project.track.nodes.map((node) => node.id));
  const originalSectionIds = new Set(project.track.sections.map((candidate) => candidate.id));
  return finalizeCandidate(project, {
    preset: definition(id),
    project: result.project,
    message: result.message,
    warnings: isSemanticInversion(id) ? ['Run Build Checks before riding this inversion.'] : [],
    affectedNodeIds: [
      ...(section ? [section.fromNodeId, section.toNodeId] : []),
      ...result.project.track.nodes
        .filter((node) => !originalNodeIds.has(node.id))
        .map((node) => node.id),
    ],
    affectedSectionIds: result.project.track.sections
      .filter(
        (candidate) => candidate.id === selectedSectionId || !originalSectionIds.has(candidate.id),
      )
      .map((candidate) => candidate.id),
  });
}
